/**
 * Обёртка над fetch.
 *
 * Главное здесь — ApiError: **текст ошибки приходит с сервера
 * и показывается пользователю как есть** (ТЗ §5). Формулировок
 * бизнес-правил на фронтенде нет и быть не должно — иначе они
 * разойдутся с серверными, и разойдутся молча.
 */

const BASE = '/api/v1';

export interface ApiErrorBody {
  error: string;
  message: string;
  details: Record<string, unknown>;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Не вошли или сессия истекла — приложение показывает вход. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Не пройдены проверки публикации: в details лежит список */
  get failedChecks(): { name: string; hint: string }[] {
    return this.code === 'event_not_ready' && Array.isArray(this.details.failed)
      ? (this.details.failed as { name: string; hint: string }[])
      : [];
  }
}

async function parse(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response;

  try {
    response = await fetch(BASE + path, {
      method,
      // Сессионная кука httpOnly — её шлёт браузер, а не код
      credentials: 'same-origin',
      headers: body instanceof FormData ? {} : { 'Content-Type': 'application/json' },
      body: body instanceof FormData ? body : body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    // Сеть отвалилась — это не ошибка правила, и текста с сервера нет
    throw new ApiError(0, 'network_error', 'Нет связи с сервером. Проверьте соединение.');
  }

  const payload = await parse(response);

  if (!response.ok) {
    const body = (payload ?? {}) as Partial<ApiErrorBody>;
    throw new ApiError(
      response.status,
      body.error ?? 'error',
      body.message ?? 'Что-то пошло не так.',
      body.details ?? {},
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  /** Тело у DELETE — для «Отменить» в M2: какие записи ленты убрать вместе с действием */
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),

  upload: <T>(path: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<T>('POST', path, form);
  },
};
